import { Alert, Button, Checkbox, Drawer, Form, Grid, Input, Select, Space } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { components } from "@qorakul/shared/api";
import { api } from "../app/api.ts";
import { ApiError, toNamePath } from "../app/apiError.ts";

type FamilyDetails = components["schemas"]["FamilyDetails"];
type GuardianDetails = components["schemas"]["GuardianDetails"];
type Relationship = components["schemas"]["GuardianRelationship"];

export const relationships: Relationship[] = ["Mother", "Father", "Grandparent", "Sibling", "LegalGuardian", "Other"];
/** Languages the API accepts today; the select is ready for more codes once the backend lists them. */
const languages = ["uz"] as const;

interface GuardianForm {
  fullName: string;
  phone: string;
  additionalPhones: string[];
  relationship: Relationship;
  preferredLanguage: string;
  isPayer: boolean;
}

interface Props {
  familyId: string;
  /** Guardian being edited, or null to add a new one. */
  guardian: GuardianDetails | null;
  open: boolean;
  onClose(): void;
  onSaved(family: FamilyDetails): void;
}

export function GuardianDrawer({ familyId, guardian, open, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<GuardianForm>();
  const [serverErrors, setServerErrors] = useState<string[]>([]);
  const editing = guardian !== null;

  const mutation = useMutation({
    mutationFn: async (values: GuardianForm) => {
      const common = {
        fullName: values.fullName.trim(),
        phone: values.phone.trim(),
        additionalPhones: (values.additionalPhones ?? []).map((p) => p.trim()).filter((p) => p.length > 0),
        relationship: values.relationship,
        preferredLanguage: values.preferredLanguage,
      };
      const result = guardian
        ? await api.PUT("/families/{id}/guardians/{guardianId}", { params: { path: { id: familyId, guardianId: guardian.id } }, body: common })
        : await api.POST("/families/{id}/guardians", { params: { path: { id: familyId } }, body: { ...common, isPayer: values.isPayer } });
      if (!result.data) throw new ApiError(result.response.status, result.error);
      return result.data;
    },
    onSuccess: async (family) => {
      await queryClient.invalidateQueries({ queryKey: ["families"] });
      setServerErrors([]);
      onSaved(family);
    },
    onError: (err: unknown) => {
      // Field errors land on their inputs; anything that has no input goes to the alert.
      const apiError = err instanceof ApiError ? err : undefined;
      const fieldErrors = Object.entries(apiError?.fieldErrors() ?? {});
      type NamePath = Parameters<typeof form.getFieldInstance>[0];
      const known: { name: NamePath; errors: string[] }[] = [];
      const unknown: string[] = [];
      for (const [path, errors] of fieldErrors) {
        const name = toNamePath(path) as NamePath;
        if (form.getFieldInstance(name) !== undefined) known.push({ name, errors });
        else unknown.push(...errors);
      }
      form.setFields(known);
      setServerErrors(fieldErrors.length === 0 ? (apiError?.messages(t("app.error")) ?? [t("app.error")]) : unknown);
    },
  });

  const initialValues: GuardianForm = guardian
    ? {
        fullName: guardian.fullName,
        phone: guardian.phone,
        additionalPhones: guardian.additionalPhones,
        relationship: guardian.relationship,
        preferredLanguage: guardian.preferredLanguage,
        isPayer: guardian.isPayer,
      }
    : { fullName: "", phone: "", additionalPhones: [], relationship: "Other", preferredLanguage: "uz", isPayer: false };

  const phoneRules = [
    { required: true, message: t("family.validation.required") },
    { pattern: /^[\d\s()+-]{9,20}$/, message: t("family.validation.phone") },
  ];

  return (
    <Drawer
      title={editing ? t("familyDetail.editGuardian") : t("family.addGuardian")}
      open={open}
      onClose={onClose}
      size={screens.md ? 520 : "100%"}
      destroyOnHidden
      extra={
        <Space>
          <Button onClick={onClose}>{t("family.cancel")}</Button>
          <Button type="primary" loading={mutation.isPending} onClick={() => form.submit()}>
            {t("family.save")}
          </Button>
        </Space>
      }
    >
      {serverErrors.length > 0 && (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} role="alert" title={serverErrors.map((m) => <div key={m}>{m}</div>)} />
      )}
      <Form<GuardianForm> form={form} layout="vertical" requiredMark={false} initialValues={initialValues} onFinish={(values) => mutation.mutate(values)}>
        <Form.Item name="fullName" label={t("family.fullName")} rules={[{ required: true, message: t("family.validation.required") }]}>
          <Input maxLength={200} />
        </Form.Item>
        <Form.Item name="phone" label={t("familyDetail.primaryPhone")} extra={t("familyDetail.primaryPhoneHint")} rules={phoneRules}>
          <Input inputMode="tel" placeholder={t("family.phoneHint")} />
        </Form.Item>
        <Form.List name="additionalPhones">
          {(fields, { add, remove }) => (
            <Form.Item label={t("familyDetail.additionalPhones")}>
              <Space orientation="vertical" style={{ width: "100%" }}>
                {fields.map((field) => (
                  <div key={field.key} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                    <Form.Item name={field.name} rules={phoneRules} style={{ flex: 1, marginBottom: 0 }}>
                      <Input inputMode="tel" placeholder={t("family.phoneHint")} />
                    </Form.Item>
                    <Button type="text" danger icon={<DeleteOutlined />} aria-label={t("familyDetail.removePhone")} onClick={() => remove(field.name)} />
                  </div>
                ))}
                {fields.length < 5 && (
                  <Button type="dashed" icon={<PlusOutlined />} onClick={() => add("")} block>
                    {t("familyDetail.addPhone")}
                  </Button>
                )}
              </Space>
            </Form.Item>
          )}
        </Form.List>
        <div style={{ display: "grid", gridTemplateColumns: screens.sm ? "1fr 1fr" : "1fr", gap: 12 }}>
          <Form.Item name="relationship" label={t("family.relationship")}>
            <Select options={relationships.map((r) => ({ value: r, label: t(`family.relationships.${r}`) }))} />
          </Form.Item>
          <Form.Item name="preferredLanguage" label={t("familyDetail.language")}>
            <Select options={languages.map((code) => ({ value: code, label: t(`familyDetail.languages.${code}`) }))} />
          </Form.Item>
        </div>
        {!editing && (
          <Form.Item name="isPayer" valuePropName="checked" extra={t("familyDetail.makePayerOnAddHint")}>
            <Checkbox>{t("familyDetail.makePayerOnAdd")}</Checkbox>
          </Form.Item>
        )}
      </Form>
    </Drawer>
  );
}
